/**
 * voiceMode — Ally Voice Mode client service (pilot, feature-flagged).
 *
 * Two responsibilities:
 *
 * 1. Availability probe: `probeVoiceAvailability()` reads `voice.enabled`
 *    from GET /api/config. The flag is computed server-side per user
 *    (VOICE_MODE_ENABLED && pilot allow-list), so the client renders ZERO
 *    voice UI when it comes back false — no rollout tells for non-pilot
 *    users (spec §5.4 / §6.5).
 *
 * 2. `VoiceSessionController` — the session lifecycle state machine:
 *
 *        idle → requesting_mic → connecting → live → ending → ended
 *                      └──────────────┴────────┴──→ error
 *
 *    Flow (standard OpenAI Realtime WebRTC pattern with ephemeral tokens):
 *      a. getUserMedia({ audio: true })
 *      b. POST /api/voice/session → { sessionId, clientSecret, webrtcUrl,
 *         model, limits } (the server mints the ephemeral token; the real
 *         API key never reaches the browser)
 *      c. RTCPeerConnection: add mic track, open the "oai-events" data
 *         channel, createOffer → POST SDP to the Realtime endpoint with the
 *         ephemeral client secret → setRemoteDescription(answer)
 *      d. Remote audio track plays through a detached <audio> element.
 *      e. Heartbeat POSTs on the server-provided interval keep the usage
 *         record alive; a heartbeat that reports capReached ends the session.
 *      f. end() closes RTC + POSTs /end; a pagehide listener sends the end
 *         request with fetch keepalive so tab-close still closes usage.
 *
 * All network/RTC/media dependencies are constructor-injectable so the unit
 * tests run with zero real network and zero real WebRTC.
 */
import { authenticatedFetch } from './authenticatedFetch';
import { getAuthHeaders } from '../stores/authStore';

// --- Availability probe ------------------------------------------------------

let availabilityCache: boolean | null = null;

export interface ProbeOptions {
  fetchImpl?: typeof fetch;
  force?: boolean;
}

/**
 * Returns true only when /api/config reports voice.enabled for the current
 * user. Result is cached for the app lifetime (flag flips require a reload —
 * acceptable for the pilot). Errors and non-OK responses resolve to false
 * WITHOUT caching, so a transient failure doesn't lock voice off all session.
 */
export async function probeVoiceAvailability(options: ProbeOptions = {}): Promise<boolean> {
  if (!options.force && availabilityCache !== null) {
    return availabilityCache;
  }

  const fetchImpl = options.fetchImpl ?? authenticatedFetch;
  try {
    const response = await fetchImpl('/api/config', { headers: getAuthHeaders() });
    if (!response.ok) {
      return false;
    }
    const payload = await response.json().catch(() => null);
    availabilityCache = Boolean(payload?.voice?.enabled);
    return availabilityCache;
  } catch {
    return false;
  }
}

export function resetVoiceAvailabilityCache(): void {
  availabilityCache = null;
}

// --- Session controller --------------------------------------------------------

export type VoiceSessionState =
  | 'idle'
  | 'requesting_mic'
  | 'connecting'
  | 'live'
  | 'ending'
  | 'ended'
  | 'error';

export type VoiceErrorCode =
  | 'mic_denied'
  | 'mic_unavailable'
  | 'unavailable'      // flag off / not in pilot (404/403)
  | 'cap_exceeded'     // 429 or capReached heartbeat
  | 'at_capacity'      // 503 concurrent/cost guard
  | 'provider_error'   // mint/SDP/OpenAI failures
  | 'network'          // connection dropped mid-session
  | 'unknown';

export interface VoiceError {
  code: VoiceErrorCode;
  message: string;
}

export interface VoiceTranscriptEvent {
  kind: 'user' | 'agent';
  text: string;
  final: boolean;
  itemId: string;
}

export interface VoiceControllerCallbacks {
  onStateChange?: (state: VoiceSessionState, error: VoiceError | null) => void;
  onTranscript?: (event: VoiceTranscriptEvent) => void;
  onAgentSpeaking?: (speaking: boolean) => void;
}

export interface VoiceControllerDeps {
  /** Authenticated fetch for Ally API calls (relative URLs). */
  apiFetch?: typeof fetch;
  /** Plain fetch for the direct SDP exchange with the Realtime endpoint. */
  realtimeFetch?: typeof fetch;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createPeerConnection?: () => RTCPeerConnection;
  createAudioElement?: () => HTMLAudioElement;
  /** Window-ish event target for pagehide registration (tests inject a stub). */
  windowTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null;
}

export interface StartSessionOptions {
  agentId?: string | null;
  conversationId?: string | null;
}

interface SessionInfo {
  sessionId: string;
  clientSecret: string;
  webrtcUrl: string;
  model: string;
  heartbeatIntervalSeconds: number;
}

const DEFAULT_HEARTBEAT_SECONDS = 30;
const CAP_MESSAGE_FALLBACK = 'You have reached your voice limit. You can continue by typing.';
const GENERIC_UNAVAILABLE = 'Voice mode is temporarily unavailable. Please try again in a few minutes.';

function defaultGetUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new DOMException('Media devices unavailable', 'NotSupportedError'));
  }
  return navigator.mediaDevices.getUserMedia(constraints);
}

export class VoiceSessionController {
  private readonly apiFetch: typeof fetch;
  private readonly realtimeFetch: typeof fetch;
  private readonly getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  private readonly createPeerConnection: () => RTCPeerConnection;
  private readonly createAudioElement: () => HTMLAudioElement;
  private readonly windowTarget: Pick<Window, 'addEventListener' | 'removeEventListener'> | null;
  private readonly callbacks: VoiceControllerCallbacks;

  private stateValue: VoiceSessionState = 'idle';
  private errorValue: VoiceError | null = null;
  private session: SessionInfo | null = null;
  private stream: MediaStream | null = null;
  private pc: RTCPeerConnection | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatMisses = 0;
  private agentTranscriptBuffers = new Map<string, string>();
  private pagehideHandler: (() => void) | null = null;
  private endRequested = false;

  constructor(callbacks: VoiceControllerCallbacks = {}, deps: VoiceControllerDeps = {}) {
    this.callbacks = callbacks;
    this.apiFetch = deps.apiFetch ?? (authenticatedFetch as typeof fetch);
    this.realtimeFetch = deps.realtimeFetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
    this.getUserMedia = deps.getUserMedia ?? defaultGetUserMedia;
    this.createPeerConnection = deps.createPeerConnection ?? (() => new RTCPeerConnection());
    this.createAudioElement = deps.createAudioElement ?? (() => new Audio());
    this.windowTarget = deps.windowTarget === undefined
      ? (typeof window === 'undefined' ? null : window)
      : deps.windowTarget;
  }

  get state(): VoiceSessionState {
    return this.stateValue;
  }

  get error(): VoiceError | null {
    return this.errorValue;
  }

  get sessionId(): string | null {
    return this.session?.sessionId ?? null;
  }

  private setState(state: VoiceSessionState, error: VoiceError | null = null): void {
    this.stateValue = state;
    this.errorValue = error;
    this.callbacks.onStateChange?.(state, error);
  }

  private fail(code: VoiceErrorCode, message: string): void {
    this.teardownRtc();
    this.setState('error', { code, message });
  }

  /** Reset from ended/error back to idle so the button can restart. */
  reset(): void {
    if (this.stateValue === 'ended' || this.stateValue === 'error') {
      this.setState('idle');
    }
  }

  async start(options: StartSessionOptions = {}): Promise<void> {
    if (this.stateValue !== 'idle' && this.stateValue !== 'ended' && this.stateValue !== 'error') {
      return;
    }
    this.endRequested = false;
    this.agentTranscriptBuffers.clear();
    this.heartbeatMisses = 0;

    // 1. Microphone
    this.setState('requesting_mic');
    try {
      this.stream = await this.getUserMedia({ audio: true });
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.fail('mic_denied', 'Microphone access is required for voice mode. Check your browser settings.');
      } else {
        this.fail('mic_unavailable', 'No usable microphone was found for voice mode.');
      }
      return;
    }

    // 2. Mint an ephemeral Realtime session server-side
    this.setState('connecting');
    let response: Response;
    try {
      response = await this.apiFetch('/api/voice/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          agentId: options.agentId ?? null,
          conversationId: options.conversationId ?? null,
        }),
      });
    } catch {
      this.fail('network', 'Voice connection failed. Check your network and try again.');
      return;
    }

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const serverMessage = typeof payload?.error === 'string' ? payload.error : null;
      if (response.status === 404 || response.status === 403) {
        // Flag off / not in pilot — treat as unavailable, render nothing loud.
        this.fail('unavailable', serverMessage ?? 'Voice mode is not available for your account.');
      } else if (response.status === 429) {
        this.fail('cap_exceeded', serverMessage ?? CAP_MESSAGE_FALLBACK);
      } else if (response.status === 503) {
        this.fail('at_capacity', serverMessage ?? GENERIC_UNAVAILABLE);
      } else {
        this.fail('provider_error', serverMessage ?? GENERIC_UNAVAILABLE);
      }
      return;
    }

    const payload = await response.json().catch(() => null);
    const data = payload?.data;
    const clientSecret = data?.clientSecret?.value;
    const webrtcUrl = data?.realtime?.webrtcUrl;
    if (!data?.sessionId || !clientSecret || !webrtcUrl) {
      this.fail('provider_error', GENERIC_UNAVAILABLE);
      return;
    }

    this.session = {
      sessionId: data.sessionId,
      clientSecret,
      webrtcUrl,
      model: data.model || '',
      heartbeatIntervalSeconds:
        Number(data.limits?.heartbeatIntervalSeconds) > 0
          ? Number(data.limits.heartbeatIntervalSeconds)
          : DEFAULT_HEARTBEAT_SECONDS,
    };

    // 3. WebRTC with the Realtime endpoint
    try {
      await this.establishWebRtc();
    } catch {
      void this.notifyServerEnd('error');
      this.fail('provider_error', GENERIC_UNAVAILABLE);
      return;
    }

    if (this.endRequested) {
      // User bailed while we were connecting.
      await this.end('user_ended');
      return;
    }

    this.setState('live');
    this.startHeartbeat();
    this.registerPagehide();
  }

  private async establishWebRtc(): Promise<void> {
    if (!this.session || !this.stream) throw new Error('no session');

    const pc = this.createPeerConnection();
    this.pc = pc;

    for (const track of this.stream.getTracks()) {
      pc.addTrack(track, this.stream);
    }

    const dataChannel = pc.createDataChannel('oai-events');
    dataChannel.onmessage = (event: MessageEvent) => {
      this.handleRealtimeEvent(typeof event.data === 'string' ? event.data : '');
    };

    pc.ontrack = (event: RTCTrackEvent) => {
      const audioEl = this.audioEl ?? this.createAudioElement();
      this.audioEl = audioEl;
      audioEl.autoplay = true;
      audioEl.srcObject = event.streams?.[0] ?? new MediaStream([event.track]);
      void audioEl.play?.()?.catch?.(() => undefined);
    };

    pc.onconnectionstatechange = () => {
      const connectionState = pc.connectionState;
      if ((connectionState === 'failed' || connectionState === 'closed' || connectionState === 'disconnected')
        && this.stateValue === 'live') {
        this.handleNetworkDrop();
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const sdpResponse = await this.realtimeFetch(
      `${this.session.webrtcUrl}?model=${encodeURIComponent(this.session.model)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.session.clientSecret}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp ?? '',
      },
    );

    if (!sdpResponse.ok) {
      throw new Error(`SDP exchange failed: ${sdpResponse.status}`);
    }

    const answerSdp = await sdpResponse.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp } as RTCSessionDescriptionInit);
  }

  // --- Realtime data channel events ---------------------------------------

  private handleRealtimeEvent(raw: string): void {
    let event: Record<string, unknown> & { type?: string };
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }

    switch (event.type) {
      case 'conversation.item.input_audio_transcription.completed': {
        const text = typeof event.transcript === 'string' ? event.transcript.trim() : '';
        if (text) {
          this.callbacks.onTranscript?.({
            kind: 'user',
            text,
            final: true,
            itemId: String(event.item_id ?? `voice-user-${Date.now()}`),
          });
        }
        break;
      }
      case 'response.audio_transcript.delta': {
        const responseId = String(event.response_id ?? 'current');
        const buffered = (this.agentTranscriptBuffers.get(responseId) ?? '')
          + (typeof event.delta === 'string' ? event.delta : '');
        this.agentTranscriptBuffers.set(responseId, buffered);
        this.callbacks.onAgentSpeaking?.(true);
        this.callbacks.onTranscript?.({
          kind: 'agent',
          text: buffered,
          final: false,
          itemId: responseId,
        });
        break;
      }
      case 'response.audio_transcript.done': {
        const responseId = String(event.response_id ?? 'current');
        const text = typeof event.transcript === 'string' && event.transcript.trim()
          ? event.transcript.trim()
          : (this.agentTranscriptBuffers.get(responseId) ?? '').trim();
        this.agentTranscriptBuffers.delete(responseId);
        if (text) {
          this.callbacks.onTranscript?.({ kind: 'agent', text, final: true, itemId: responseId });
        }
        break;
      }
      case 'output_audio_buffer.stopped':
      case 'response.done': {
        this.callbacks.onAgentSpeaking?.(false);
        break;
      }
      case 'error': {
        // Non-fatal provider event; log only. Fatal failures surface through
        // the connection state change instead.
        console.warn('[voice] realtime error event:', event);
        break;
      }
      default:
        break;
    }
  }

  // --- Heartbeat -----------------------------------------------------------

  private startHeartbeat(): void {
    if (!this.session) return;
    const intervalMs = this.session.heartbeatIntervalSeconds * 1000;
    this.heartbeatTimer = setInterval(() => {
      void this.sendHeartbeat();
    }, intervalMs);
  }

  private async sendHeartbeat(): Promise<void> {
    if (!this.session || this.stateValue !== 'live') return;
    let response: Response;
    try {
      response = await this.apiFetch(`/api/voice/session/${this.session.sessionId}/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
    } catch {
      this.heartbeatMisses += 1;
      if (this.heartbeatMisses >= 2) {
        this.handleNetworkDrop();
      }
      return;
    }

    this.heartbeatMisses = 0;
    if (!response.ok) {
      // 404 = session already closed server-side (stale sweep); stop cleanly.
      this.stopLocalSession();
      this.setState('ended');
      return;
    }

    const payload = await response.json().catch(() => null);
    const data = payload?.data;
    if (data && data.active === false) {
      this.stopLocalSession();
      if (data.capReached) {
        this.setState('error', { code: 'cap_exceeded', message: CAP_MESSAGE_FALLBACK });
      } else {
        this.setState('ended');
      }
    }
  }

  private handleNetworkDrop(): void {
    if (this.stateValue !== 'live') return;
    void this.notifyServerEnd('error');
    this.stopLocalSession();
    this.setState('error', {
      code: 'network',
      message: 'Voice connection lost. You can continue by typing or start voice mode again.',
    });
  }

  // --- End -----------------------------------------------------------------

  async end(reason: 'user_ended' | 'error' = 'user_ended'): Promise<void> {
    if (this.stateValue === 'requesting_mic' || this.stateValue === 'connecting') {
      // start() checks endRequested after connect completes.
      this.endRequested = true;
      return;
    }
    if (this.stateValue !== 'live') return;

    this.setState('ending');
    this.stopLocalSession();
    await this.notifyServerEnd(reason);
    this.setState('ended');
  }

  private async notifyServerEnd(reason: string, keepalive = false): Promise<void> {
    const session = this.session;
    if (!session) return;
    try {
      await this.apiFetch(`/api/voice/session/${session.sessionId}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ endReason: reason }),
        ...(keepalive ? { keepalive: true } : {}),
      });
    } catch {
      // Best effort — the server's stale-heartbeat sweep closes the record.
    }
  }

  private stopLocalSession(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.unregisterPagehide();
    this.teardownRtc();
  }

  private teardownRtc(): void {
    try {
      this.pc?.close();
    } catch {
      // already closed
    }
    this.pc = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    if (this.audioEl) {
      try {
        this.audioEl.pause?.();
      } catch {
        // ignore
      }
      this.audioEl.srcObject = null;
      this.audioEl = null;
    }
  }

  // --- Tab close -------------------------------------------------------------

  private registerPagehide(): void {
    if (!this.windowTarget || this.pagehideHandler) return;
    this.pagehideHandler = () => {
      // fetch keepalive survives page unload (sendBeacon can't carry the
      // Authorization header, so keepalive fetch is the end "beacon" here).
      void this.notifyServerEnd('user_ended', true);
      this.stopLocalSession();
      this.stateValue = 'ended';
    };
    this.windowTarget.addEventListener('pagehide', this.pagehideHandler);
  }

  private unregisterPagehide(): void {
    if (this.windowTarget && this.pagehideHandler) {
      this.windowTarget.removeEventListener('pagehide', this.pagehideHandler);
    }
    this.pagehideHandler = null;
  }

  /** Full cleanup for component unmount. */
  destroy(): void {
    if (this.stateValue === 'live') {
      void this.notifyServerEnd('user_ended');
    }
    this.stopLocalSession();
    this.stateValue = 'idle';
    this.errorValue = null;
  }
}
